import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  List,
  Grid3X3,
  AlertCircle,
  Eye,
  Edit,
  UserCog,
  Key,
  UserX,
} from 'lucide-react';
import { ROLE_LABELS } from '../../data/mockData';

const roleColors = {
  admin: 'bg-red-50 text-red-700 border-red-200',
  supreme_manager: 'bg-purple-50 text-purple-700 border-purple-200',
  manager: 'bg-blue-50 text-blue-700 border-blue-200',
  quality_checker: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  field_executive: 'bg-amber-50 text-amber-700 border-amber-200',
  vendor: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  client: 'bg-pink-50 text-pink-700 border-pink-200',
  business_partner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  universal_qc: 'bg-teal-50 text-teal-700 border-teal-200',
  data_entry_operator: 'bg-orange-50 text-orange-700 border-orange-200',
  vendor_fe: 'bg-sky-50 text-sky-700 border-sky-200',
};

export default function UserListingView({
  submenu,
  searchQuery,
  setSearchQuery,
  roleFilter,
  setRoleFilter,
  regionFilter,
  setRegionFilter,
  managerFilter,
  setManagerFilter,
  vendorFilter,
  setVendorFilter,
  viewMode,
  setViewMode,
  filteredUsers,
  filterOptions,
  setActiveDossierUser,
  setActiveDossierTab,
  setEditingUser,
  setShowEditDialog,
  triggerChangeRole,
  handleResetPassword,
  handleToggleStatus,
}) {
  return (
    <div className="space-y-4">
      {/* Card Filters Block */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="space-y-1">
          <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <Input
              className="pl-8 bg-slate-50/50 border-slate-200 h-9 text-xs"
              placeholder="ID, Name, Contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Role</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="bg-slate-50/50 border-slate-200 h-9 text-xs">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Region</Label>
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="bg-slate-50/50 border-slate-200 h-9 text-xs">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Regions</SelectItem>
              {filterOptions.regions.map(r => (
                <SelectItem key={r} value={r}>{r} Region</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Parent/Manager</Label>
          <Select value={managerFilter} onValueChange={setManagerFilter}>
            <SelectTrigger className="bg-slate-50/50 border-slate-200 h-9 text-xs">
              <SelectValue placeholder="All Creators" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Managers</SelectItem>
              {filterOptions.managers.map(mgr => (
                <SelectItem key={mgr} value={mgr}>{mgr}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1 col-span-1">
          <Label className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Status</Label>
          <div className="text-xs bg-slate-100/70 border border-slate-200 h-9 rounded-md flex items-center px-3 font-semibold text-slate-600 capitalize">
            {submenu} Filter
          </div>
        </div>

        <div className="space-y-1 flex items-end">
          <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg border border-slate-200 w-full justify-between">
            <Button
              variant={viewMode === 'table' ? 'white' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="flex-1 py-1 h-7 text-xs font-semibold"
            >
              <List className="w-3.5 h-3.5 mr-1" /> Table
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'white' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="flex-1 py-1 h-7 text-xs font-semibold"
            >
              <Grid3X3 className="w-3.5 h-3.5 mr-1" /> Cards
            </Button>
          </div>
        </div>
      </div>

      {/* Listing table */}
      {filteredUsers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-3 animate-bounce" />
          <h3 className="text-sm font-bold text-slate-800">No users found</h3>
          <p className="text-xs text-slate-400 mt-1">Try modifying your search or dropdown filters.</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-5 py-3">Profile / Name</th>
                  <th className="px-5 py-3">Role Badge</th>
                  <th className="px-5 py-3">Parent User</th>
                  <th className="px-5 py-3">Contact info</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Emp ID</th>
                  <th className="px-5 py-3">Created Date</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 ring-2 ring-slate-100">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback className="bg-slate-100 text-slate-500">{user.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-slate-900 hover:text-blue-600 cursor-pointer" onClick={() => { setActiveDossierUser(user); setActiveDossierTab('basic'); }}>{user.name}</p>
                          <p className="text-[10px] text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${roleColors[user.role] || 'bg-slate-50 text-slate-700'}`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-slate-600 font-medium">{user.parentName || 'System'}</span>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-slate-700">{user.phone}</p>
                      <p className="text-[9px] text-slate-400">Region: {user.region}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${user.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                        {user.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-[10px] text-slate-500">
                      {user.employeeId}
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {user.joinDate}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-blue-600 hover:bg-blue-50" onClick={() => { setActiveDossierUser(user); setActiveDossierTab('basic'); }}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-slate-800 hover:bg-slate-100" onClick={() => { setEditingUser(user); setShowEditDialog(true); }}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-purple-600 hover:bg-purple-50" onClick={() => triggerChangeRole(user)}>
                          <UserCog className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-amber-600 hover:bg-amber-50" onClick={() => handleResetPassword(user)}>
                          <Key className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className={`w-7 h-7 ${user.status === 'active' ? 'text-rose-500 hover:bg-rose-50' : 'text-emerald-500 hover:bg-emerald-50'}`} onClick={() => handleToggleStatus(user.id)}>
                          <UserX className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Cards View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map(user => (
            <div key={user.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition relative flex flex-col justify-between border-t-4 border-t-blue-500">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 ring-2 ring-slate-100">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback className="bg-slate-100 text-slate-500">{user.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-bold text-slate-800 hover:text-blue-600 cursor-pointer" onClick={() => { setActiveDossierUser(user); setActiveDossierTab('basic'); }}>{user.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold border mt-1 ${roleColors[user.role] || 'bg-slate-50 text-slate-700'}`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </div>
                  </div>

                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${user.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    {user.status.toUpperCase()}
                  </span>
                </div>

                <div className="space-y-1.5 py-3 border-t border-b border-slate-100 text-xs text-slate-600 my-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Employee ID:</span>
                    <span className="font-mono text-slate-700 font-medium">{user.employeeId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Email ID:</span>
                    <span className="text-slate-700 truncate max-w-[160px]">{user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Contact No:</span>
                    <span className="text-slate-700">{user.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Parent Hierarchy:</span>
                    <span className="text-slate-800 font-semibold">{user.parentName || 'System'}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-1 pt-1 mt-2">
                <span className="text-[10px] text-slate-400">Joined: {user.joinDate}</span>
                <div className="flex items-center gap-0.5">
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-blue-600" onClick={() => { setActiveDossierUser(user); setActiveDossierTab('basic'); }}>
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-slate-800" onClick={() => { setEditingUser(user); setShowEditDialog(true); }}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-purple-600" onClick={() => triggerChangeRole(user)}>
                    <UserCog className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-500 hover:text-rose-500" onClick={() => handleToggleStatus(user.id)}>
                    <UserX className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
